import {ApolloLink, fromPromise} from "@apollo/client"
import {setContext} from "@apollo/client/link/context"
import {onError} from "@apollo/client/link/error"

import {ensureFreshToken, forceRefreshToken, getRoleToken, isUnauthorized} from "./utils/token"

/**
 * How a call authenticates itself.
 *
 * @property {boolean} [isApiKey] - Send the token as an `apiKey` header instead of a bearer + role token.
 * @property {string} [odmToken] - Explicit role token, overriding the selected role's one.
 * @property {string} [customToken] - Explicit token to send as `apiKey`.
 */
export type AuthHeaderOptions = {
    isApiKey?: boolean
    odmToken?: string
    customToken?: string
}

/**
 * Link that renews the access token when it is close to expiring, then stamps
 * the auth headers on the operation.
 *
 * Being async is the point: it lets the renewal complete *before* the request
 * goes out, so a call never leaves with a token the API would refuse.
 *
 * @param {AuthHeaderOptions} [options] - How this call authenticates itself.
 * @returns {ApolloLink} The link.
 */
export const createAuthLink = (options: AuthHeaderOptions = {}): ApolloLink =>
    setContext(async (_operation, {headers = {}}) => {
        const token = await ensureFreshToken()

        if (options.isApiKey) {
            return {headers: {...headers, apiKey: options.customToken || token}}
        }

        const authHeaders: Record<string, string> = {...headers}

        // Omit the header entirely rather than sending "Bearer " with nothing
        // after it, which some gateways answer 500 to instead of 401.
        if (token) {
            authHeaders.authorization = `Bearer ${token}`
        }

        const roleToken = options.odmToken || getRoleToken()
        if (roleToken) {
            authHeaders.roleToken = roleToken
        }

        return {headers: authHeaders}
    })

/**
 * Link that retries once, with a freshly minted token, whatever the API refused
 * with a 401.
 *
 * A 401 means the token we sent was rejected — so its stated expiry cannot be
 * trusted and a plain `ensureFreshToken` would not renew it. One forced renewal
 * is attempted, then the operation is replayed; the auth link below re-stamps
 * the new token on the way down.
 *
 * The retry happens at most once per operation. A permanently refused token
 * would otherwise loop forever against the API.
 *
 * @returns {ApolloLink} The link. Place it above `createAuthLink` in the chain.
 */
export const createUnauthorizedRetryLink = (): ApolloLink =>
    onError(({networkError, operation, forward}) => {
        if (!isUnauthorized(networkError) || operation.getContext().authRetried) {
            return
        }

        operation.setContext({authRetried: true})

        return fromPromise(forceRefreshToken()).flatMap(() => forward(operation))
    })
